import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Radio, Lock, Crown, Zap, CalendarClock, Users } from "lucide-react";
import { LIVE } from "../config/site";
import { E, useEditable, toNum } from "../context/ContentContext";
import { API } from "../lib/scroll";
import { WriteToKimberly } from "./PayHelp";
import { Progress } from "./ui/progress";
import { EASE } from "./Reveal";

const pad = (n) => String(n).padStart(2, "0");
const DAYS = ["dom", "lun", "mar", "mer", "gio", "ven", "sab"];
const MONTHS = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
export const fmtLiveDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]} · ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Stato diretta dal server (data, posti reali) + countdown allineato all'orologio del server.
export const useLive = (every = 30000) => {
  const [live, setLive] = useState(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    let on = true;
    const load = () => axios.get(`${API}/live`).then((r) => { if (!on) return; setLive(r.data); setOffset(new Date(r.data.server_now).getTime() - Date.now()); }).catch(() => {});
    load();
    const t = setInterval(load, every);
    return () => { on = false; clearInterval(t); };
  }, [every]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const left = live?.starts_at ? Math.max(0, new Date(live.starts_at).getTime() - (now + offset)) : 0;
  const s = Math.floor(left / 1000);
  return { live, left, d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
};

const Digit = ({ v, label }) => (
  <div className="flex flex-col items-center">
    <div className="relative h-12 w-12 overflow-hidden rounded-xl border border-[rgba(255,77,109,0.35)] bg-[#0b0b0d] sm:h-14 sm:w-14">
      <AnimatePresence mode="popLayout">
        <motion.span key={v} initial={{ y: -14, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 14, opacity: 0 }} transition={{ duration: 0.3, ease: EASE }} className="absolute inset-0 flex items-center justify-center font-display text-xl text-[#f5f2ee] tabular-nums sm:text-2xl">{pad(v)}</motion.span>
      </AnimatePresence>
    </div>
    <span className="mt-1 text-[9px] uppercase tracking-[0.2em] text-[#8a847d]">{label}</span>
  </div>
);

// Diretta esplicita: data e ora reali, countdown, posti limitati davvero (scalano con gli ordini). Accesso a pagamento.
export const LiveSection = ({ onRequest }) => {
  const get = useEditable();
  const price = toNum(get("live.price", String(LIVE.price)), LIVE.price);
  const name = get("live.name", LIVE.name);
  const { live, d, h, m, s } = useLive();

  const phase = live?.phase || "none";
  const seatsTotal = live?.seats_total || 0;
  const seatsLeft = live?.seats_left || 0;
  const soldOut = phase !== "none" && phase !== "ended" && seatsLeft <= 0;
  const bookable = (phase === "scheduled" || phase === "live") && seatsLeft > 0;
  const pct = seatsTotal ? Math.round(((seatsTotal - seatsLeft) / seatsTotal) * 100) : 0;
  const buy = () => onRequest({ type: "diretta", item: name, price, kind: "offer", id: "diretta" });

  const headLabel = phase === "live" ? <E k="live.labelLive">In diretta ora</E> : phase === "scheduled" ? <E k="live.labelNext">Prossima diretta</E> : <E k="live.labelNone">Diretta</E>;

  return (
    <section id="diretta" data-testid="live-section" className="relative bg-[#0b0b0d] py-14 sm:py-20">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-center gap-3">
          <span className={`live-dot ${phase === "live" ? "" : "opacity-60"}`} />
          <span className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#ff4d6d]">{headLabel}</span>
          {live?.starts_at && phase !== "ended" && <span className="flex items-center gap-1 text-[11px] text-[#f5f2ee]" data-testid="live-date"><CalendarClock className="h-3.5 w-3.5 text-[color:var(--gold)]" /> {fmtLiveDate(live.starts_at)}</span>}
          {live && seatsTotal > 0 && phase !== "none" && phase !== "ended" && (
            <span className={`flex items-center gap-1 text-[11px] ${soldOut ? "text-[#ff8a8a]" : "text-[#b9b2aa]"}`} data-testid="live-seats"><Users className="h-3.5 w-3.5" /> {soldOut ? "posti esauriti" : `${seatsLeft} ${seatsLeft === 1 ? "posto libero" : "posti liberi"} su ${seatsTotal}`}</span>
          )}
        </div>
        <h2 className="mt-3 font-display text-3xl font-semibold uppercase leading-none text-[#f5f2ee] sm:text-5xl"><E k="live.title">Diretta</E> <span className="text-gold-sheen italic"><E k="live.title2">esplicita.</E></span></h2>

        <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.55, ease: EASE }} className="card-special mt-6 grid overflow-hidden rounded-[26px] md:grid-cols-[1.1fr_1fr]" data-testid="live-card">
          <button type="button" data-testid="live-preview" onClick={bookable ? buy : undefined} className="relative aspect-[16/11] overflow-hidden md:aspect-auto md:min-h-[360px]" aria-label={bookable ? "Prenota il posto in diretta" : "Anteprima diretta"}>
            <img src={LIVE.img} alt="" aria-hidden draggable={false} className="absolute inset-0 h-full w-full scale-105 object-cover" />
            <span className="absolute inset-0 bg-gradient-to-t from-[#0b0b0d] via-[#0b0b0d]/30 to-transparent" />
            {phase === "live" ? (
              <span className="absolute left-4 top-4 flex items-center gap-2 rounded-full bg-[#ff4d6d] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white"><Radio className="h-3 w-3" /> Live</span>
            ) : live?.starts_at && phase === "scheduled" ? (
              <span className="absolute left-4 top-4 flex items-center gap-2 rounded-full bg-[#0b0b0d]/85 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[#f5f2ee]"><CalendarClock className="h-3 w-3 text-[#ff4d6d]" /> {fmtLiveDate(live.starts_at)}</span>
            ) : null}
            <span className="absolute right-4 top-4 rounded-md border border-[rgba(0,175,240,0.45)] bg-[#0b0b0d]/85 px-2 py-1 text-[10px] font-bold tracking-[0.12em] text-[color:var(--gold)]">18+</span>
            <span className="absolute inset-0 flex flex-col items-center justify-center gap-2">
              <span className="flex h-16 w-16 items-center justify-center rounded-full border border-[rgba(255,77,109,0.6)] bg-[rgba(11,11,13,0.6)] shadow-[0_0_36px_rgba(255,77,109,0.4)]"><Lock className="h-7 w-7 text-[#ff8aa0]" /></span>
              <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#f5f2ee]/90"><E k="live.lock">Solo membri VEROFAN</E></span>
            </span>
          </button>
          <div className="flex flex-col justify-between p-6 sm:p-8">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--rose)] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#0b0b0d]"><Crown className="h-3 w-3" /> <E k="live.badge">Riservata VEROFAN</E></span>
              <h3 className="mt-4 font-display text-3xl italic text-[#f5f2ee] sm:text-4xl"><E k="live.name">{LIVE.name}</E></h3>
              <p className="mt-2 text-sm text-[#b9b2aa]"><E k="live.desc">Senza tagli, senza filtri. Entri, mi guardi, mi scrivi in chat. Pochi posti, poi chiudo.</E></p>

              {phase === "scheduled" && (
                <div className="mt-5" data-testid="live-countdown">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#8a847d]"><E k="live.countdownLabel">Si apre tra</E></span>
                  <div className="mt-2 flex gap-2">
                    {d > 0 && <Digit v={d} label="giorni" />}
                    <Digit v={h} label="ore" />
                    <Digit v={m} label="min" />
                    <Digit v={s} label="sec" />
                  </div>
                </div>
              )}
              {phase === "live" && <p className="mt-5 flex items-center gap-2 text-sm text-[#ff8aa0]" data-testid="live-now"><span className="live-dot" /> Sono in diretta adesso: entra finche' c'e' posto.</p>}
              {(phase === "none" || phase === "ended") && <p className="mt-5 text-sm text-[#b9b2aa]" data-testid="live-none"><E k="live.none">Prossima data in arrivo. Scrivimi e ti avviso per prima.</E></p>}

              {seatsTotal > 0 && (phase === "scheduled" || phase === "live") && (
                <div className="mt-5" data-testid="live-seats-bar">
                  <div className="flex items-center justify-between text-xs"><span className="text-[#b9b2aa]">Posti</span><span className={soldOut ? "text-[#ff8a8a]" : "text-[#f5f2ee]"}>{soldOut ? "Esauriti" : `${seatsLeft} su ${seatsTotal}`}</span></div>
                  <Progress value={pct} className="mt-1.5 h-1.5 bg-[rgba(255,77,109,0.15)] [&>div]:bg-[#ff4d6d]" />
                </div>
              )}
            </div>
            <div className="mt-6 flex items-end justify-between gap-4">
              <div>
                <span className="block text-[10px] uppercase tracking-[0.2em] text-[#8a847d]">Accesso</span>
                <span className="text-gold-sheen font-display text-4xl tabular-nums" data-testid="live-price">€<E k="live.price">{String(LIVE.price)}</E></span>
              </div>
              {bookable ? (
                <button data-testid="live-cta" onClick={buy} className="btn-gold flex min-h-[48px] items-center gap-2 rounded-xl px-6 text-[12px] font-semibold uppercase tracking-[0.14em]">
                  <Zap className="h-4 w-4" /> {phase === "live" ? <E k="live.ctaLive">Entra ora</E> : <E k="live.cta">Prenota il posto</E>}
                </button>
              ) : (
                <WriteToKimberly item={soldOut ? `${name} (lista d'attesa)` : `${name} (avvisami)`} testId="live-write" />
              )}
            </div>
            {soldOut && <p className="mt-3 text-[11px] text-[#ff8a8a]" data-testid="live-soldout">Posti esauriti. Scrivimi per la lista d'attesa o la prossima data.</p>}
            {bookable && <p className="mt-3 text-[11px] text-[#8a847d]"><E k="live.note">Devi essere membro VEROFAN.</E> Il posto resta bloccato {live?.hold_minutes || 120} minuti dopo la prenotazione: paga entro quel tempo.</p>}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
